import { ICustomService, REPORT_DATA_SOURCE_NAME, ReportDataState } from 'store/conciergeSlice';

export interface ToolSourceDomain {
    name: string;
    concierge_domain_id: string;
    customServices?: ICustomService[];
}

/** The `source` marker posted with a turn whose selected source is Report Data. */
export const REPORT_DATA_SOURCE_PARAM = 'source';

/**
 * The `source` value posted with an All Sources turn. Where search is a user's only source, All Sources and an
 * explicit Tiles pick post the same tool list; the back end reads this marker, never the list, to tell them apart.
 */
export const ALL_SOURCES_SOURCE_NAME = 'all_sources';

const serviceTool = (domain: ToolSourceDomain, service: ICustomService): string => {
    const domainId =
        service.domain_id && service.domain_id > ''
            ? service.domain_id
            : domain.concierge_domain_id !== '-1'
              ? `id_${domain.concierge_domain_id}`
              : null;
    return domainId ? `${service.name}|${domainId}` : service.name;
};

const addDomainTools = (result: Set<string>, domain: ToolSourceDomain) => {
    domain.customServices?.forEach((service) => result.add(serviceTool(domain, service)));
};

export const isReportDataSelected = (selectedDomains: string[]): boolean =>
    selectedDomains.includes(REPORT_DATA_SOURCE_NAME);

const REPORT_DATA_INFO_KEYS: Record<ReportDataState, string> = {
    no_report: 'report_data.info_no_report',
    enabled: 'report_data.info_enabled',
    no_access: 'report_data.info_no_access',
};

/** The `app/concierge/widget` key of the Report Data hovercard text for a state (Concierge_Panel.md §6.4). */
export const reportDataInfoKey = (state: ReportDataState): string => REPORT_DATA_INFO_KEYS[state];

/**
 * The tool grant for a turn, from the source selector. The backend reads the selected source off
 * this list (a Tiles-only turn is served by search), so the list IS the selection:
 *  - a Domain: its own services;
 *  - Report Data: nothing — the turn is scoped by the `source=report_data` marker, not by its tools,
 *    and no Domain, documentation or tile-search tool belongs to it (Concierge_Panel.md §6.4);
 *  - All Sources (nothing selected, or nothing with services): everything.
 */
export function toolsForSelectedSources(selectedDomains: string[], availableDomains: ToolSourceDomain[]): string[] {
    const result = new Set<string>();

    if (isReportDataSelected(selectedDomains)) {
        return [];
    }

    selectedDomains.forEach((name) => {
        const domain = availableDomains.find((d) => d.name === name);
        if (domain) {
            addDomainTools(result, domain);
        }
    });

    if (isAllSourcesSelected(selectedDomains, availableDomains)) {
        availableDomains.forEach((d) => addDomainTools(result, d));
    }

    return Array.from(result);
}

/** All Sources: not Report Data, and no selected source carries a service (the grant then falls back to everything). */
export function isAllSourcesSelected(selectedDomains: string[], availableDomains: ToolSourceDomain[]): boolean {
    if (isReportDataSelected(selectedDomains)) {
        return false;
    }

    return !selectedDomains.some(
        (name) => (availableDomains.find((d) => d.name === name)?.customServices?.length ?? 0) > 0,
    );
}

/**
 * The `source` marker a turn posts: a source the caller already set, else Report Data or All Sources from the
 * selection; none for an explicit pick or a Domain Editor preview (its tools come from the previewed domain).
 */
export function sourceMarkerForTurn(
    selectedDomains: string[],
    availableDomains: ToolSourceDomain[],
    preset?: string,
    domainPreview = false,
): string | undefined {
    if (preset) {
        return preset;
    }
    if (isReportDataSelected(selectedDomains)) {
        return REPORT_DATA_SOURCE_NAME;
    }
    if (domainPreview) {
        return undefined;
    }

    return isAllSourcesSelected(selectedDomains, availableDomains) ? ALL_SOURCES_SOURCE_NAME : undefined;
}

/** Domain ids for the additional-data request: real Domains only, never the report_data pseudo-domain. */
export function domainIdsForAdditionalData(selectedDomains: string[], availableDomains: ToolSourceDomain[]): string[] {
    return selectedDomains
        .filter((name) => name !== REPORT_DATA_SOURCE_NAME)
        .map((name) => availableDomains.find((d) => d.name === name)?.concierge_domain_id)
        .filter((id): id is string => !!id && id !== '-1');
}
